:root {
  /* Parchment & ink, by day. */
  --ink: #4a3b28;
  --ink-h: #2b1e10;
  --ink-faint: #8a7a5e;
  --bg: #efe3c6;
  --bg-alt: #e4d3ac;
  --border: #bfa878;
  --panel-bg: #f3e8cd;
  --panel-bg-alt: #e8d9b4;
  --accent: #8a5a1e;
  --accent-bg: rgba(138, 90, 30, 0.1);
  --gold: #a07a2a;
  --danger: #8c2d1c;
  --danger-bg: rgba(140, 45, 28, 0.1);

  --shadow-sm: 0 1px 2px rgba(60, 40, 15, 0.16);
  --shadow-md: 0 2px 8px rgba(60, 40, 15, 0.14);
  --shadow-lg: 0 6px 22px rgba(60, 40, 15, 0.16);

  /* The parchment wash laid over a screen's illustration so ink stays legible:
     near-opaque at the top where the reading starts, a touch clearer toward the
     foot so the art can breathe. See the per-screen backdrops in App.css. */
  --content-veil: rgba(239, 227, 198, 0.9);
  --content-veil-edge: rgba(239, 227, 198, 0.7);

  --display: "Cinzel", "Trajan Pro", Georgia, serif;
  --display-deco: "MedievalSharp", "Cinzel", "Trajan Pro", Georgia, serif;
  --serif: "Cinzel", "Trajan Pro", Georgia, "Times New Roman", serif;
  --mono: ui-monospace, Consolas, monospace;

  /* Fine paper grain, laid over parchment surfaces. Generated rather than
     loaded so there is no asset to ship or cache. */
  --grain: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");

  font: 18px/1.6 var(--serif);
  color-scheme: light dark;
  color: var(--ink);
  background-color: var(--bg);
}

@media (prefers-color-scheme: dark) {
  :root {
    /* Iron & candlelight, by night. */
    --ink: #c0af8c;
    --ink-h: #f0e3c0;
    --ink-faint: #8b7f68;
    --bg: #191410;
    --bg-alt: #221a12;
    --border: #574733;
    --panel-bg: #241d15;
    --panel-bg-alt: #1e1811;
    --accent: #d1a039;
    --accent-bg: rgba(209, 160, 57, 0.14);
    --gold: #c69a3c;
    --danger: #bc5945;
    --danger-bg: rgba(188, 89, 69, 0.16);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.5);
    --shadow-md: 0 2px 10px rgba(0, 0, 0, 0.45);
    --shadow-lg: 0 6px 24px rgba(0, 0, 0, 0.5);

    --content-veil: rgba(25, 20, 16, 0.88);
    --content-veil-edge: rgba(25, 20, 16, 0.66);
  }
}

* {
  box-sizing: border-box;
}

html,
body {
  height: 100%;
}

body {
  margin: 0;
  /* This is a game UI, not a document — the arrow cursor and no drag-selecting
     of labels. Text fields opt back in below so typing and editing still work. */
  cursor: default;
  user-select: none;
  -webkit-user-select: none;
  /* Warm candlelight from above, cooler and darker at the edges — the page
     itself reads as a lit surface rather than a flat fill. */
  background-color: var(--bg);
  background-image:
    radial-gradient(ellipse 120% 70% at 50% -10%, rgba(255, 246, 220, 0.55), transparent 70%),
    radial-gradient(ellipse 100% 100% at 50% 120%, rgba(90, 62, 24, 0.16), transparent 70%),
    linear-gradient(178deg, var(--bg), var(--bg-alt));
  background-attachment: fixed;
  /* Cinzel ships no italic face, but the flavour text leans on italics
     throughout — allow the browser to synthesize the slant (real 600/700
     weights still handle bold, so weight is never synthesized). */
  font-synthesis: style;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

@media (prefers-color-scheme: dark) {
  body {
    background-image:
      radial-gradient(ellipse 120% 70% at 50% -10%, rgba(120, 88, 40, 0.3), transparent 70%),
      radial-gradient(ellipse 100% 100% at 50% 120%, rgba(0, 0, 0, 0.4), transparent 70%),
      linear-gradient(178deg, var(--bg), var(--bg-alt));
  }
}

/* Grain sits above the page fill but below all content, so every surface
   picks up the same paper tooth without each one repeating the texture. */
body::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: var(--grain);
  opacity: 0.05;
  mix-blend-mode: multiply;
}

@media (prefers-color-scheme: dark) {
  body::before {
    opacity: 0.09;
    mix-blend-mode: overlay;
  }
}

/* ----------------------------------------------------------- atmosphere */

/* The ambient light of the world, set from how far the loop has run toward the
   Day 512 deadline (see atmosphereEngine). The phase is stamped on the document
   root as data-timeofday; each phase supplies a tint colour and a strength, and
   a single fixed layer (body::after) paints them behind all content — so the
   whole scene warms at dawn, holds through the day, and cools toward an
   extinguished-sun night as the deadline nears. Layered over the light/dark
   palette rather than replacing it, so day- and night-theme both still read. */
@property --atmos-strength {
  syntax: "<number>";
  inherits: true;
  initial-value: 0;
}

/* How far the room's light has drained toward the Day 512 blackout, as 0..1
   (see getLoopProgress / App's atmosphere effect). Registered as a real number
   so it can be animated: it steps a shade darker with each passing day rather
   than snapping. Nothing on Day 1, total by the deadline. */
@property --loop-darkness {
  syntax: "<number>";
  inherits: true;
  initial-value: 0;
}

:root {
  --atmos-tint: 90, 62, 24;
  --atmos-strength: 0;
  --loop-darkness: 0;
}

:root[data-timeofday="dawn"] {
  --atmos-tint: 236, 158, 96;
  --atmos-strength: 0.1;
}

:root[data-timeofday="day"] {
  --atmos-tint: 255, 240, 205;
  --atmos-strength: 0.04;
}

:root[data-timeofday="dusk"] {
  --atmos-tint: 214, 110, 44;
  --atmos-strength: 0.14;
}

:root[data-timeofday="night"] {
  --atmos-tint: 42, 60, 96;
  --atmos-strength: 0.22;
}

@media (prefers-color-scheme: dark) {
  /* The candlelight base is already dim, so the tints press more gently. */
  :root[data-timeofday="dawn"] {
    --atmos-strength: 0.09;
  }
  :root[data-timeofday="dusk"] {
    --atmos-strength: 0.13;
  }
  :root[data-timeofday="night"] {
    --atmos-strength: 0.2;
  }
}

/* Sits above the page fill and grain but below #root, so it tints the scene
   through the mostly-transparent chrome without ever washing out the parchment
   cards, which carry their own opaque fill. Strength eases when the day crosses
   a phase boundary, so the light shifts rather than snaps. */
body::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  transition:
    --atmos-strength 1.4s ease,
    --loop-darkness 1.4s ease;
  background:
    /* The blackout wash. Painted first so it sits above the atmosphere tints in
       this same layer: at full darkness it covers the room's light entirely,
       leaving only the parchment content (which lives in #root, above this) lit.
       On Day 1 its alpha is 0, so the scene reads exactly as it always has. */
    linear-gradient(
      rgba(0, 0, 0, var(--loop-darkness)),
      rgba(0, 0, 0, var(--loop-darkness))
    ),
    radial-gradient(
      ellipse 135% 85% at 50% -5%,
      rgba(var(--atmos-tint), var(--atmos-strength)),
      transparent 80%
    ),
    radial-gradient(
      ellipse 145% 100% at 50% 120%,
      rgba(var(--atmos-tint), calc(var(--atmos-strength) * 0.9)),
      transparent 78%
    ),
    /* A faint wash across the whole page so the light of the "room" shifts as a
       whole — not just its edges — as the loop runs down toward night. */
    linear-gradient(
      rgba(var(--atmos-tint), calc(var(--atmos-strength) * 0.4)),
      rgba(var(--atmos-tint), calc(var(--atmos-strength) * 0.4))
    );
}

#root {
  position: relative;
  z-index: 1;
  height: 100%;
}

/* ----------------------------------------------------- accessibility (Phase 10)
   Player-set text scale and motion level, stamped on the root by settings.ts.
   The stylesheet is px-heavy rather than rem-based, so text scaling is done with
   `zoom` on the app tree — it magnifies text and spacing together (which is what
   the setting promises) and, unlike transform, keeps layout and scrolling sane.
   Normal scale sets nothing, so the default render is untouched. */
:root[data-text-scale="small"] #root {
  zoom: 0.9;
}
:root[data-text-scale="large"] #root {
  zoom: 1.12;
}
:root[data-text-scale="xlarge"] #root {
  zoom: 1.25;
}

/* Reduced trims animation; Off stills it. Both collapse animations and
   transitions to nothing across the whole tree — the same end the per-component
   prefers-reduced-motion blocks reach, but driven by the explicit choice so it
   wins even on a system that has not asked for reduced motion. */
:root[data-motion="reduced"] *,
:root[data-motion="reduced"] *::before,
:root[data-motion="reduced"] *::after,
:root[data-motion="none"] *,
:root[data-motion="none"] *::before,
:root[data-motion="none"] *::after {
  animation-duration: 0.001ms !important;
  animation-delay: 0.001ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.001ms !important;
  transition-delay: 0s !important;
  scroll-behavior: auto !important;
}

h1,
h2,
h3 {
  font-family: var(--display);
  color: var(--ink-h);
  text-wrap: balance;
}

h1 {
  font-family: var(--display-deco);
  font-size: 32px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.6px;
  margin: 0 0 18px;
  text-shadow: 0 1px 0 rgba(255, 252, 240, 0.4);
}

@media (prefers-color-scheme: dark) {
  h1 {
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  }
}

h2 {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--accent);
  margin: 30px 0 12px;
  padding-bottom: 9px;
  position: relative;
}

/* A drawn rule that fades out to the right, like a pen stroke running dry —
   replaces the hard full-width underline. */
h2::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--border), transparent 85%);
}

/* On a content screen the rule is penned on left-to-right as the screen opens,
   so a heading arrives as a stroke of the pen rather than a printed line. Only
   on screens (the sidebar's quiet labels stay still). */
.screen h2::after {
  transform-origin: left center;
  animation: rule-draw 0.55s ease-out both;
}

@keyframes rule-draw {
  from {
    transform: scaleX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .screen h2::after {
    animation: none;
  }
}

h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
}

p {
  margin: 0 0 10px;
  max-width: 68ch;
}

a {
  color: var(--accent);
}

button {
  font: 500 14px/1.35 var(--display);
  letter-spacing: 0.4px;
  color: var(--ink-h);
  text-align: left;
  padding: 9px 15px;
  border: 1px solid var(--border);
  border-radius: 3px;
  cursor: pointer;
  background-color: var(--panel-bg);
  /* Two layers, painted within the button (so nothing overflows and markers,
     which blank their background, are untouched): a diagonal gilt sheen parked
     off the right edge, and the standing top-light. On hover the sheen sweeps
     across, the way light catches raised gilt when a page is tilted. */
  background-image:
    linear-gradient(
      115deg,
      transparent 44%,
      rgba(255, 250, 235, 0.42) 50%,
      transparent 56%
    ),
    linear-gradient(180deg, rgba(255, 250, 235, 0.5), rgba(0, 0, 0, 0.04));
  background-repeat: no-repeat;
  background-size: 230% 100%, auto;
  /* The sheen rests parked off the right edge and only ever travels on the way
     in (see the hover animation below), so leaving the button never drags the
     highlight back across it. */
  background-position: 135% 0, 0 0;
  box-shadow: var(--shadow-sm), inset 0 1px 0 rgba(255, 252, 240, 0.35);
  transition:
    border-color 0.14s ease,
    background-color 0.14s ease,
    box-shadow 0.14s ease,
    transform 0.12s ease;
}

button:hover:not(:disabled) {
  border-color: var(--gold);
  background-color: var(--accent-bg);
  box-shadow: var(--shadow-md), inset 0 1px 0 rgba(255, 252, 240, 0.4);
}

/* A tactile raise on hover, pressed back down on click, and a one-shot gilt
   sweep as the pointer arrives — but never on map markers, whose position is
   meaningful and must not drift, and which paint no background to sweep. */
button:hover:not(:disabled):not(.map-marker) {
  transform: translateY(-1px);
  animation: btn-sheen 0.7s ease;
}

/* Runs once on hover-in and ends parked back off the right edge, so there is no
   return sweep when the mouse leaves. */
@keyframes btn-sheen {
  from {
    background-position: 135% 0, 0 0;
  }
  to {
    background-position: -35% 0, 0 0;
  }
}

button:active:not(:disabled):not(.map-marker) {
  transform: translateY(1px);
  box-shadow: inset 0 1px 3px rgba(60, 40, 15, 0.22);
}

/* A player who has asked for stillness keeps the colour and shadow cues but
   loses the sheen sweep and the lift. */
@media (prefers-reduced-motion: reduce) {
  button:hover:not(:disabled):not(.map-marker) {
    transform: none;
    animation: none;
  }
}

button:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 2px;
}

button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

/* Text fields are the one place the document behaviour is still wanted: a
   caret, an I-beam, and the freedom to select what you have typed. */
input,
textarea,
[contenteditable="true"] {
  cursor: text;
  user-select: text;
  -webkit-user-select: text;
}

/* Anything that acts like a control gets the hand, even when it isn't a
   <button> (links, ARIA buttons). */
a[href],
[role="button"]:not(:disabled),
label {
  cursor: pointer;
}

ul {
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
}

::selection {
  background: var(--accent-bg);
  color: var(--ink-h);
}

/* Slim, unobtrusive scrollbars so the fixed panes don't get chrome-heavy
   gutters cutting through the parchment. */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
}

*::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}

*::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 5px;
}

*::-webkit-scrollbar-track {
  background: transparent;
}
